import { CaseReportType, CaseSlaStatus } from '@compass/client/types/cases_analytics';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SupportedTimeZone } from '@/constants/date';
import { getDefaultDateRange } from '@/pages/analytics/utils/dateWindow';
import {
  parseIdListParam,
  parseMultiValueParam,
  serializeMultiValueParam,
} from '@/utils/urlParams';

const DEFAULT_RANGE_DAYS = 7;

const REPORT_TYPES = [CaseReportType.CASE_REPORT_TYPE_AE, CaseReportType.CASE_REPORT_TYPE_PQC];
const SLA_STATUSES = [CaseSlaStatus.CASE_SLA_STATUS_WITHIN, CaseSlaStatus.CASE_SLA_STATUS_BREACHED];

interface UseCasesAnalyticsFiltersStateOptions {
  /** Dashboard-wide timezone from the store (no default: analytics must not fork one). */
  timeZone: SupportedTimeZone;
}

/**
 * Single owner of URL <-> state for the AE / PQC analytics filters. Built like
 * `useAnalyticsFiltersState`: search params with `replace`, enums as their numbers
 * (`report_types=1,2`, `sla_status=2`), and the last 7 days when no dates are set.
 */
export const useCasesAnalyticsFiltersState = ({
  timeZone,
}: UseCasesAnalyticsFiltersStateOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const programIds = useMemo(
    () => parseIdListParam(searchParams.get('program_ids')),
    [searchParams],
  );
  const reportTypes = useMemo(
    () =>
      parseMultiValueParam(searchParams.get('report_types'))
        .map(Number)
        .filter((n): n is CaseReportType => REPORT_TYPES.includes(n)),
    [searchParams],
  );
  const slaParam = Number(searchParams.get('sla_status'));
  const slaStatus = SLA_STATUSES.includes(slaParam)
    ? (slaParam as CaseSlaStatus)
    : CaseSlaStatus.CASE_SLA_STATUS_UNSPECIFIED;

  const defaultRange = useMemo(() => getDefaultDateRange(DEFAULT_RANGE_DAYS, timeZone), [timeZone]);
  const start = searchParams.get('start') || defaultRange.start;
  const end = searchParams.get('end') || defaultRange.end;

  const updateSearchParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            if (value == null || value === '') params.delete(key);
            else params.set(key, value);
          });
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Keep the URL canonical (shareable, and what saved views capture) once defaults apply.
  const hasDates = !!searchParams.get('start') && !!searchParams.get('end');
  useEffect(() => {
    if (!hasDates) updateSearchParams(defaultRange);
  }, [hasDates, defaultRange, updateSearchParams]);

  const setProgramIds = useCallback(
    (next: string[]) => updateSearchParams({ program_ids: serializeMultiValueParam(next) }),
    [updateSearchParams],
  );
  const setReportTypes = useCallback(
    (next: CaseReportType[]) =>
      updateSearchParams({ report_types: serializeMultiValueParam(next.map(String)) }),
    [updateSearchParams],
  );
  const setSlaStatus = useCallback(
    (next: CaseSlaStatus) =>
      updateSearchParams({
        sla_status: next === CaseSlaStatus.CASE_SLA_STATUS_UNSPECIFIED ? undefined : String(next),
      }),
    [updateSearchParams],
  );
  const setDateRange = useCallback(
    (range: { start: string; end: string }) =>
      updateSearchParams({ start: range.start || undefined, end: range.end || undefined }),
    [updateSearchParams],
  );

  return {
    programIds,
    setProgramIds,
    reportTypes,
    setReportTypes,
    slaStatus,
    setSlaStatus,
    start,
    end,
    setDateRange,
  };
};
